import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Save, Plus, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DAYS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];

/** Editable weekly hours + custom menu categories for a restaurant. */
export const RestaurantHoursEditor = ({ token, restaurant, onSaved }) => {
  const initial = restaurant?.weekly_hours || {};
  const initialCats = restaurant?.menu_categories || [];
  const [weekly, setWeekly] = useState(() => {
    const out = {};
    for (let i = 0; i < 7; i++) {
      out[String(i)] = initial[String(i)] || [{ open: '09:00', close: '23:00' }];
    }
    return out;
  });
  const [categories, setCategories] = useState(initialCats);
  const [newCat, setNewCat] = useState('');
  const [isOpen, setIsOpen] = useState(restaurant?.is_open !== false);
  const [saving, setSaving] = useState(false);

  const openDay = (day) => setWeekly({ ...weekly, [day]: [{ open: '09:00', close: '23:00' }] });
  const closeDay = (day) => setWeekly({ ...weekly, [day]: [] });

  // Single (no break) period — operates on slot[0]
  const setSingleField = (day, field, v) => {
    const slots = (weekly[day] || []).map((s) => ({ ...s }));
    if (slots.length === 0) return;
    slots[0] = { ...slots[0], [field]: v };
    setWeekly({ ...weekly, [day]: slots });
  };

  // Split schedule (horário repartido) — two periods with a break in between.
  // slot[0] = morning (open -> pause_start), slot[1] = afternoon (pause_end -> close)
  const enableBreak = (day) => {
    const slots = weekly[day] || [];
    const open = slots[0]?.open || '09:00';
    const close = slots[slots.length - 1]?.close || '23:00';
    setWeekly({ ...weekly, [day]: [{ open, close: '15:00' }, { open: '18:00', close }] });
  };
  const disableBreak = (day) => {
    const slots = weekly[day] || [];
    const open = slots[0]?.open || '09:00';
    const close = slots[slots.length - 1]?.close || '23:00';
    setWeekly({ ...weekly, [day]: [{ open, close }] });
  };
  const setBreakField = (day, which, v) => {
    const slots = (weekly[day] || []).map((s) => ({ ...s }));
    if (slots.length < 2) return;
    if (which === 'open') slots[0].open = v;
    else if (which === 'pause_start') slots[0].close = v;
    else if (which === 'pause_end') slots[1].open = v;
    else if (which === 'close') slots[1].close = v;
    setWeekly({ ...weekly, [day]: slots });
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/restaurants/${restaurant.restaurant_id}/hours`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({
          weekly_hours: weekly,
          menu_categories: categories,
          is_open: isOpen,
        }),
      });
      if (r.ok) {
        toast.success('Horários e categorias guardados');
        onSaved?.();
      } else {
        toast.error('Erro a guardar');
      }
    } catch (e) { toast.error('Erro de rede'); }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="restaurant-hours-editor">
      <div className="flex items-center gap-3 p-3 bg-[#FFF7ED] rounded-xl">
        <Switch checked={isOpen} onCheckedChange={setIsOpen} data-testid="rest-is-open" />
        <Label>
          Restaurante aberto (manual — desligue para fechar agora, independentemente do horário)
        </Label>
      </div>

      <div className="space-y-2">
        {DAYS.map((label, i) => {
          const day = String(i);
          const slots = weekly[day] || [];
          const closed = slots.length === 0;
          const hasBreak = slots.length >= 2;
          return (
            <div key={day} className="border rounded-xl p-3 space-y-3" data-testid={`day-${day}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium text-[#004E64]">{label}</span>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={!closed}
                    onCheckedChange={(v) => (v ? openDay(day) : closeDay(day))}
                    data-testid={`day-${day}-open-toggle`}
                  />
                  <span className="text-sm text-[#9FA2B4] w-16">{closed ? 'Fechado' : 'Aberto'}</span>
                </div>
              </div>

              {!closed && (
                <>
                  <div className="flex items-center gap-2 p-2 bg-[#FFF7ED] rounded-lg">
                    <Switch
                      checked={hasBreak}
                      onCheckedChange={(v) => (v ? enableBreak(day) : disableBreak(day))}
                      data-testid={`day-${day}-break-toggle`}
                    />
                    <Label className="text-sm">Horário repartido (com pausa)</Label>
                  </div>

                  {!hasBreak ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-[#9FA2B4] w-12">Abre</span>
                      <Input
                        type="time" value={slots[0]?.open || ''}
                        onChange={(e) => setSingleField(day, 'open', e.target.value)}
                        className="w-32" data-testid={`day-${day}-open`}
                      />
                      <span>→</span>
                      <span className="text-sm text-[#9FA2B4]">Fecha</span>
                      <Input
                        type="time" value={slots[0]?.close || ''}
                        onChange={(e) => setSingleField(day, 'close', e.target.value)}
                        className="w-32" data-testid={`day-${day}-close`}
                      />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-[#9FA2B4] w-24">Abre</span>
                        <Input
                          type="time" value={slots[0]?.open || ''}
                          onChange={(e) => setBreakField(day, 'open', e.target.value)}
                          className="w-32" data-testid={`day-${day}-open`}
                        />
                        <span>→</span>
                        <span className="text-sm text-[#9FA2B4]">Início da pausa</span>
                        <Input
                          type="time" value={slots[0]?.close || ''}
                          onChange={(e) => setBreakField(day, 'pause_start', e.target.value)}
                          className="w-32" data-testid={`day-${day}-pause-start`}
                        />
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-[#9FA2B4] w-24">Fim da pausa</span>
                        <Input
                          type="time" value={slots[1]?.open || ''}
                          onChange={(e) => setBreakField(day, 'pause_end', e.target.value)}
                          className="w-32" data-testid={`day-${day}-pause-end`}
                        />
                        <span>→</span>
                        <span className="text-sm text-[#9FA2B4]">Fecha</span>
                        <Input
                          type="time" value={slots[1]?.close || ''}
                          onChange={(e) => setBreakField(day, 'close', e.target.value)}
                          className="w-32" data-testid={`day-${day}-close`}
                        />
                      </div>
                      <p className="text-xs text-[#9FA2B4]">
                        Durante a pausa ({slots[0]?.close} – {slots[1]?.open}) o restaurante fica fechado para pedidos.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Categories */}
      <div className="border rounded-xl p-3 space-y-2" data-testid="menu-categories-editor">
        <Label className="font-medium">Categorias do menu</Label>
        <p className="text-xs text-[#9FA2B4]">
          Ex: <em>Pratos, Bebidas, Sobremesas</em>. Depois usa no editor do menu para organizar os items.
        </p>
        <div className="flex flex-wrap gap-2">
          {categories.map((c, i) => (
            <div key={i} className="inline-flex items-center gap-1 bg-[#F4F1EA] rounded-full pl-3 pr-1 py-1 text-sm">
              {c}
              <button onClick={() => setCategories(categories.filter((_, idx) => idx !== i))} className="p-1 text-[#9FA2B4] hover:text-red-500">
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Nova categoria" className="flex-1" />
          <Button onClick={() => { if (newCat.trim()) { setCategories([...categories, newCat.trim()]); setNewCat(''); } }}>
            <Plus size={14} />
          </Button>
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full">
        <Save size={14} className="mr-2" /> {saving ? 'A guardar...' : 'Guardar horários + categorias'}
      </Button>
    </div>
  );
};

export default RestaurantHoursEditor;
